import { PRICE_REPRESENTATION_MULTIPLIER } from './Pricing.ts';
import type { Product, ProductDiscount } from './Product.ts';

/**
 * These types are create manually, based on the promotion schema.
 * @see `promotions.schema.json`
 */

export type PromotionItem = {
  promotionType: PromotionType;
  /** The text describing this promotion. */
  description: string;
};

/** The type of data coming from the CMS payload */
type CMSPromotionGroupData =
  | { promotions: PromotionItem[] }
  | Pick<
      PromotionGroup,
      | 'description'
      | 'name'
      | 'subText'
      | 'products'
      | 'termsUrl'
      | 'buyOptionButton'
    >;
export type PromotionContent = {
  promotionGroups: Array<CMSPromotionGroupData>;
};

export type PromotionGroup = {
  /** Name of the promotion group - This field is for internal use only, not used by the component. */
  name: string;
  /** Description of the promotion group */
  description: string;
  /** One or more promotions which are available to products in this group. */
  promotions: StandardizedPromotionLineItem[];
  /** Additional text displayed below the list of promotions. */
  subText?: string;
  /** List of full product IDs (long SKUs) to which this promotion group applies. */
  products: string[];
  /** Absolute URL for the T&C's content */
  termsUrl: string;
  /** Optional custom text for the buy option button */
  buyOptionButton?: string;
};

/** A type which describes all types of promotion in our domain */
export type StandardizedPromotionLineItem = {
  description: string;
  type: PromotionTypes;
  modifier?: number;
};

export type PromotionType =
  | FixedPriceRebate
  | PercentageRebate
  | FreePromotion
  | OtherPromotionType;

export type PromotionTypes = 'FIXED_PRICE' | 'PERCENTAGE' | 'OTHER' | 'FREE';

/** Generic promo rule container */
interface PromotionRule {
  type: PromotionTypes;
}

export interface FixedPriceRebate extends PromotionRule {
  type: 'FIXED_PRICE';
  /** Enter the fixed amount which has been deducted from the product price. Use period as decimal separator and no thousands separator. */
  discountAmount: string;
}
export interface PercentageRebate extends PromotionRule {
  type: 'PERCENTAGE';
  /** Enter a whole number which will be the percentage discount which has been applied. */
  percentageAmount: number;
}
export interface FreePromotion extends PromotionRule {
  type: 'FREE';
}
export interface OtherPromotionType extends PromotionRule {
  type: 'OTHER';
}

export function getPromotionsForProduct(
  promoGroups: PromotionGroup[],
  productId: string
): PromotionGroup[] {
  return promoGroups.filter(({ products }) => products.includes(productId));
}

export function promoIsFixed(
  promoType: PromotionType
): promoType is FixedPriceRebate {
  return promoType.type === 'FIXED_PRICE';
}
export function promoIsPercentage(
  promoType: PromotionType
): promoType is PercentageRebate {
  return promoType.type === 'PERCENTAGE';
}
export function promoIsFree(
  promoType: PromotionType
): promoType is FreePromotion {
  return promoType.type === 'FREE';
}
export function promoIsOther(
  promoType: PromotionType
): promoType is OtherPromotionType {
  return promoType.type === 'OTHER';
}

/**
 * Decide how to handle the product price saving based on the promotion type
 */
export function getPromotionPriceSaving(
  type: StandardizedPromotionLineItem['type'],
  modifier?: StandardizedPromotionLineItem['modifier']
): ProductDiscount | undefined {
  if (type === 'FIXED_PRICE') {
    return {
      type,
      discountAmount: modifier
    };
  }

  if (type === 'PERCENTAGE') {
    return {
      type,
      percentageAmount: modifier
    };
  }

  return undefined;
}

/**
 * Calculates the price of the product after any discounts, based on original price and discount config
 * If not discount then undefined is returned
 */
export function getDiscountedPromoPrice(
  price: number,
  discount?: ProductDiscount
): number | undefined {
  if (undefined === discount) {
    // no discount
    return undefined;
  }

  const { type, discountAmount, percentageAmount } = discount;

  if (type === 'PERCENTAGE') {
    if (percentageAmount === undefined) {
      return undefined;
    }
    // 20 (percent off) needs to be: price * 0.8
    const multiplier = (100 - percentageAmount) / 100;
    return price * multiplier;
  }

  if (type === 'FIXED_PRICE') {
    if (discountAmount === undefined) {
      return undefined;
    }
    // treat discount as a simple subtraction
    return price - discountAmount * PRICE_REPRESENTATION_MULTIPLIER;
  }

  return undefined;
}

/**
 * Finds the lowest priced promotion for a product
 */
export function getLowestPricedPromotion(product: Product): number | undefined {
  if (!product.promotions || product.promotions.length === 0) {
    return undefined;
  }

  const { price: originalPrice } = product.pricing;
  const discountedPrices: number[] = [];
  const {
    promotions: [promoGroups]
  } = product;

  promoGroups.promotions.forEach((promotion) => {
    if (promotion.type === 'FIXED_PRICE' || promotion.type === 'PERCENTAGE') {
      const discountedPrice = getDiscountedPromoPrice(
        originalPrice,
        getPromotionPriceSaving(promotion.type, promotion.modifier)
      );
      if (discountedPrice !== undefined) {
        discountedPrices.push(discountedPrice);
      }
    }
  });

  if (discountedPrices.length === 0) {
    return undefined;
  }

  return Math.min(...discountedPrices);
}
